import type { AdminUserSummary } from '@molt/shared';
import { Link } from '@tanstack/react-router';

import { Avatar, profileName } from '@/components/Avatar';
import { RelativeTime } from '@/components/RelativeTime';
import { useInterfaceLanguage } from '@/lib/interface-language';
import {
  ADMIN_TD_CLASS,
  ADMIN_TH_CLASS,
  ADMIN_THEAD_CLASS,
  ADMIN_TR_CLASS,
  AdminPill,
  AdminTableFrame,
} from './AdminConsole';

/**
 * SA-9 — the Users tab. Deactivated and soft-deleted accounts are listed (SA-8),
 * so the status column says which, beside the platform admin marker; the
 * account's name opens `/admin/users/:id`, where the account actions live.
 */

export interface AdminUserTableProps {
  readonly items: readonly AdminUserSummary[];
}

export function AdminUserTable({ items }: AdminUserTableProps) {
  const { t } = useInterfaceLanguage();
  return (
    <AdminTableFrame label={t('admin.users.tableLabel')}>
      <thead className={ADMIN_THEAD_CLASS}>
        <tr>
          <th className={ADMIN_TH_CLASS}>{t('admin.users.column.account')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.users.column.email')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.users.column.status')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.users.column.workspaces')}</th>
          <th className={ADMIN_TH_CLASS}>{t('admin.users.column.created')}</th>
        </tr>
      </thead>
      <tbody>
        {items.map((user) => (
          <tr key={user.id} className={ADMIN_TR_CLASS}>
            <td className={ADMIN_TD_CLASS}>
              <div className="flex w-56 min-w-0 items-center gap-2">
                <Avatar profile={user} size="sm" decorative />
                <div className="flex min-w-0 flex-col">
                  <Link
                    to="/admin/users/$userId"
                    params={{ userId: user.id }}
                    className="truncate font-medium text-ink underline-offset-2 hover:underline"
                  >
                    {profileName(user)}
                  </Link>
                  {user.username === '' ? null : (
                    <span className="truncate text-xs text-ink-muted">
                      @{user.username}
                    </span>
                  )}
                </div>
              </div>
            </td>
            <td className={`${ADMIN_TD_CLASS} min-w-56 text-ink wrap-anywhere`}>
              {user.email}
            </td>
            <td className={ADMIN_TD_CLASS}>
              <AdminUserStatus user={user} />
            </td>
            <td className={`${ADMIN_TD_CLASS} tabular-nums text-ink`}>
              {user.workspaceCount}
            </td>
            <td className={`${ADMIN_TD_CLASS} whitespace-nowrap text-ink-muted`}>
              <RelativeTime value={user.createdAt} />
            </td>
          </tr>
        ))}
      </tbody>
    </AdminTableFrame>
  );
}

/** Lifecycle first (deleted outranks deactivated), then the platform admin marker. */
export function AdminUserStatus({
  user,
}: {
  readonly user: Pick<AdminUserSummary, 'deletedAt' | 'deactivatedAt' | 'isSuper'>;
}) {
  const { t } = useInterfaceLanguage();
  return (
    <div className="flex flex-wrap gap-1">
      {user.deletedAt !== null ? (
        <AdminPill tone="bad">{t('admin.users.status.deleted')}</AdminPill>
      ) : user.deactivatedAt !== null ? (
        <AdminPill tone="warn">{t('admin.users.status.deactivated')}</AdminPill>
      ) : (
        <AdminPill>{t('admin.users.status.active')}</AdminPill>
      )}
      {user.isSuper ? (
        <AdminPill tone="accent">{t('admin.users.status.superadmin')}</AdminPill>
      ) : null}
    </div>
  );
}
